// =============================================================
// 📁 src/pages/planificateur/ArchivesPage.tsx
// 💎 Version Platinum PRO 2025 — Archives des descentes (corrigée UI sombre finale)
// =============================================================

import React, { useEffect, useState } from "react";
import {
  Table,
  Input,
  Button,
  Space,
  DatePicker,
  Select,
  Pagination,
  Tag,
  Tooltip,
  message,
  Spin,
} from "antd";
import {
  SearchOutlined,
  FilePdfOutlined,
  FileExcelOutlined,
  ReloadOutlined,
  InboxOutlined,
  ArrowLeftOutlined,
  HomeOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { saveAs } from "file-saver";
import { useNavigate } from "react-router-dom";
import { api } from "../../../lib/axios";

const { RangePicker } = DatePicker;
const { Option } = Select;

const ArchivesPage: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [archives, setArchives] = useState<any[]>([]);
  const [search, setSearch] = useState("");
  const [statut, setStatut] = useState<string | undefined>();
  const [dates, setDates] = useState<any[]>([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [limit] = useState(10);

  // =============================================================
  // 📥 Charger les archives
  // =============================================================
  const fetchArchives = async () => {
    try {
      setLoading(true);
      const params: Record<string, any> = { page, limit };
      if (search) params.search = search;
      if (statut) params.statut = statut;
      if (dates.length === 2) {
        params.dateDebut = dayjs(dates[0]).format("YYYY-MM-DD");
        params.dateFin = dayjs(dates[1]).format("YYYY-MM-DD");
      }

      const res = await api.get("/planificateur/archives", { params });
      setArchives(res.data.archives || []);
      setTotal(res.data.total || 0);
    } catch (err) {
      console.error(err);
      message.error("Erreur lors du chargement des archives.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchArchives();
  }, [page, statut]);

  // =============================================================
  // 📦 Archivage manuel
  // =============================================================
  const lancerArchivage = async () => {
    try {
      if (!window.confirm("Souhaitez-vous archiver toutes les descentes terminées ?")) return;
      message.loading("Archivage en cours...", 1.5);
      const res = await api.post("/planificateur/archive");
      if (res.data.success) {
        message.success(`${res.data.archivedCount || 0} descentes archivées avec succès.`);
        fetchArchives();
      } else message.warning(res.data.message || "Aucune descente à archiver.");
    } catch {
      message.error("Erreur lors de l’archivage.");
    }
  };

  // =============================================================
  // 📁 Exports PDF / Excel
  // =============================================================
  const handleExport = async (type: "pdf" | "excel") => {
    try {
      message.loading(`Génération ${type.toUpperCase()}...`);
      const params: Record<string, any> = { exportType: type };
      if (search) params.search = search;
      if (statut) params.statut = statut;
      if (dates.length === 2) {
        params.dateDebut = dayjs(dates[0]).format("YYYY-MM-DD");
        params.dateFin = dayjs(dates[1]).format("YYYY-MM-DD");
      }

      const res = await api.get("/planificateur/archives", {
        params,
        responseType: "blob",
      });

      const fileName = `archives_descente_${dayjs().format(
        "YYYY-MM-DD"
      )}.${type === "pdf" ? "pdf" : "xlsx"}`;
      saveAs(res.data, fileName);
      message.success(`${type.toUpperCase()} exporté avec succès.`);
    } catch {
      message.error(`Erreur export ${type.toUpperCase()}`);
    }
  };

  // =============================================================
  // 🧩 Colonnes du tableau
  // =============================================================
  const columns = [
    { title: "Titre", dataIndex: "titre", key: "titre" },
    { title: "Zone / Quartier", dataIndex: "quartier", key: "quartier" },
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
      render: (t: string) => <Tag color="blue">{t || "N/A"}</Tag>,
    },
    { title: "Agent", dataIndex: "agentNom", key: "agentNom" },
    {
      title: "Statut",
      dataIndex: "statut",
      key: "statut",
      render: (s: string) => {
        const color =
          s?.toLowerCase().includes("term") || s?.toLowerCase().includes("val")
            ? "green"
            : s?.toLowerCase().includes("plan")
            ? "orange"
            : "blue";
        return <Tag color={color}>{s}</Tag>;
      },
    },
    {
      title: "Date descente",
      dataIndex: "date",
      key: "date",
      render: (d: string) => dayjs(d).format("DD/MM/YYYY"),
    },
    {
      title: "Archivée le",
      dataIndex: "archivedAt",
      key: "archivedAt",
      render: (d: string) => (d ? dayjs(d).format("DD/MM/YYYY HH:mm") : "—"),
    },
  ];

  // =============================================================
  // 🎨 Rendu principal
  // =============================================================
  return (
    <div className="min-h-screen bg-slate-900 text-gray-100 p-6 space-y-6">
      {/* 🌈 Thème sombre lisible Ant Design */}
      <style>{`
        /* --- Inputs et champs principaux --- */
        .ant-input,
        .ant-select-selector,
        .ant-picker {
          background-color: #1e293b !important;
          color: #f1f5f9 !important;
          border-color: #3b82f6 !important;
        }
        .ant-input::placeholder {
          color: #9ca3af !important;
        }

        /* --- Sélecteurs déroulants (dropdown) --- */
        .ant-select-item-option {
          background-color: #1e293b !important;
          color: #f8fafc !important;
        }
        .ant-select-item-option-active {
          background-color: #2563eb !important;
          color: #fff !important;
        }

        /* --- Calendrier --- */
        .ant-picker-panel,
        .ant-picker-content {
          background-color: #1e293b !important;
          color: #f8fafc !important;
        }
        .ant-picker-cell-inner {
          color: #f1f5f9 !important;
        }
        .ant-picker-cell-disabled .ant-picker-cell-inner {
          color: #64748b !important;
        }
        .ant-picker-cell.ant-picker-cell-selected .ant-picker-cell-inner {
          background-color: #2563eb !important;
          color: white !important;
        }
        .ant-picker-cell.ant-picker-cell-today .ant-picker-cell-inner::before {
          border-color: #3b82f6 !important;
        }

        /* --- Focus states --- */
        .ant-picker-focused,
        .ant-select-focused,
        .ant-input-focused {
          border-color: #60a5fa !important;
          box-shadow: 0 0 0 2px rgba(59,130,246,0.3) !important;
        }

        /* --- Dropdown calendrier --- */
        .ant-picker-dropdown {
          background-color: #0f172a !important;
          border: 1px solid #334155 !important;
          box-shadow: 0 8px 24px rgba(0,0,0,0.5) !important;
        }
      `}</style>

      {/* 🔙 Navigation */}
      <div className="flex justify-between items-center mb-4">
        <div className="flex items-center gap-3">
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate(-1)}
            style={{ background: "#334155", color: "#fff", border: "none" }}
          >
            ⬅ Retour
          </Button>

          <Button
            icon={<HomeOutlined />}
            onClick={() => navigate("/dashboard")}
            style={{ background: "#2563EB", color: "#fff", border: "none" }}
          >
            🏠 Dashboard
          </Button>
        </div>

        <Tooltip title="Archiver toutes les descentes terminées">
          <Button
            icon={<InboxOutlined />}
            onClick={lancerArchivage}
            style={{ background: "#f57c00", color: "#fff", border: "none" }}
          >
            📦 Lancer l’archivage
          </Button>
        </Tooltip>
      </div>

      {/* 🧭 Barre de filtres */}
      <div className="bg-slate-700 p-4 rounded-lg border border-slate-600 shadow">
        <Space wrap>
          <Input
            placeholder="🔎 Rechercher (titre, zone, agent)"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            allowClear
            onPressEnter={fetchArchives}
            style={{
              width: 260,
              background: "#1e293b",
              color: "#fff",
              border: "1px solid #2563eb",
              fontWeight: 500,
            }}
          />

          <Select
            placeholder="Statut"
            allowClear
            value={statut}
            onChange={(v) => setStatut(v)}
            style={{
              width: 180,
              background: "#1e293b",
              color: "#fff",
              border: "1px solid #2563eb",
              fontWeight: 500,
            }}
            styles={{
              popup: { root: { backgroundColor: "#1e293b", color: "#fff" } },
            }}
          >
            <Option value="Terminée">Terminée</Option>
            <Option value="Planifiée">Planifiée</Option>
            <Option value="Complétée">Complétée</Option>
            <Option value="Validée">Validée</Option>
          </Select>

          <RangePicker
            onChange={(val) => setDates(val || [])}
            format="DD/MM/YYYY"
            style={{
              width: 280,
              backgroundColor: "#1e293b",
              border: "1px solid #2563eb",
              borderRadius: 6,
              color: "#fff",
              fontWeight: 500,
              padding: "8px 12px",
            }}
            placeholder={["Date début", "Date fin"]}
          />

          <Button
            icon={<SearchOutlined />}
            onClick={fetchArchives}
            style={{
              background: "#2563eb",
              color: "#fff",
              border: "none",
              fontWeight: 600,
              height: 40,
            }}
          >
            Rechercher
          </Button>

          <Tooltip title="Rafraîchir">
            <Button
              icon={<ReloadOutlined />}
              onClick={() => {
                setSearch("");
                setStatut(undefined);
                setDates([]);
                setPage(1);
                fetchArchives();
              }}
              style={{
                background: "#1e293b",
                color: "#93c5fd",
                border: "1px solid #2563eb",
                height: 40,
              }}
            />
          </Tooltip>

          <Tooltip title="Exporter PDF">
            <Button
              icon={<FilePdfOutlined />}
              style={{
                background: "#dc2626",
                color: "#fff",
                fontWeight: 600,
                height: 40,
              }}
              onClick={() => handleExport("pdf")}
            >
              PDF
            </Button>
          </Tooltip>

          <Tooltip title="Exporter Excel">
            <Button
              icon={<FileExcelOutlined />}
              style={{
                background: "#16a34a",
                color: "#fff",
                fontWeight: 600,
                height: 40,
              }}
              onClick={() => handleExport("excel")}
            >
              Excel
            </Button>
          </Tooltip>
        </Space>
      </div>

      {/* 📋 Tableau principal */}
      <div className="bg-slate-800 rounded-lg border border-slate-700 shadow overflow-hidden">
        <Spin spinning={loading} tip="Chargement...">
          <Table
            columns={columns}
            dataSource={archives}
            rowKey="id"
            pagination={false}
            style={{ background: "#0f172a", color: "#fff" }}
          />
        </Spin>

        <div className="flex justify-end p-3 bg-slate-800 border-t border-slate-700">
          <Pagination
            current={page}
            total={total}
            pageSize={limit}
            showSizeChanger={false}
            onChange={(p) => setPage(p)}
            style={{
              background: "#1e293b",
              borderRadius: 6,
              padding: "4px 10px",
            }}
          />
        </div>
      </div>

      <div className="text-center text-gray-500 text-xs pt-4">
        © {new Date().getFullYear()} EspoirAfriqueSas — Tous droits réservés.
      </div>
    </div>
  );
};

export default ArchivesPage;